import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { learnAssetMeta, learnAssetUrl, letterAsset, normalizeAssetKey } from "../../media/learnAssets";
import { useDragEngine } from "../../interaction/useDragEngine";
import { worldContainingLetter } from "../../../../utils/learnLetterProgress";

/** Mulberry32-based shuffle; returns a new array. */
export function seededShuffle(items, seed = 1) {
  const arr = [...(items || [])];
  let t = (Number(seed) || 1) >>> 0;
  const rand = () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Fit-to-arena: the largest tile multiplier (`max` down to `min`, in `step`s)
 * at which `count` square tiles wrap inside a `w`×`h` px box, with a gap of
 * `gap` tiles between them. Returns `{ scale, cols, rows, fits }`; until the
 * box is measured (`w`/`h`/`tile` = 0) it answers `max` so nothing jumps.
 * `maxCols` caps the columns (2×2 for four choices, never 3+1).
 */
export function fitTiles({ count, w, h, tile, max = 1.1, min = 0.85, gap = 0.14, step = 0.05, maxCols = Infinity }) {
  const n = Math.max(1, count || 0);
  if (!w || !h || !tile) return { scale: max, cols: Math.min(n, maxCols), rows: Math.ceil(n / Math.min(n, maxCols)), fits: true };
  const layout = (s) => {
    const px = tile * s;
    const g = tile * gap;
    const cols = Math.max(1, Math.min(n, maxCols, Math.floor((w + g) / (px + g))));
    const rows = Math.ceil(n / cols);
    return { scale: Number(s.toFixed(3)), cols, rows, fits: rows * px + (rows - 1) * g <= h && cols * px + (cols - 1) * g <= w };
  };
  for (let s = max; s >= min - 1e-6; s -= step) {
    const l = layout(s);
    if (l.fits) return l;
  }
  return layout(min);
}

export function itemKey(item, idx = 0) {
  return `${item?.word || item?.id || "item"}-${item?.assetName || idx}`;
}

/**
 * Hosted URL for a letter-game asset name. Manifest-aware: cutouts that live
 * in another letter's folder (letter M playing `f_distractor_moon`), root
 * sprites (`pip_course_2d`) and writing-town cutouts (`call_sun`) resolve to
 * the folder that actually holds them. Legacy mini-game cutouts (ch_/asf_/fm_)
 * still come from /public.
 */
export function resolveAsset(letter, assetName) {
  if (!assetName) return null;
  const raw = String(assetName);
  if (raw.startsWith("/") || raw.startsWith("http")) return raw;
  const key = normalizeAssetKey(raw);
  const L = String(letter || "a").toLowerCase();
  const candidates = [`letters/${L}/${key}`];
  const own = key.match(/^([a-z])_/);
  if (own && own[1] !== L) candidates.push(`letters/${own[1]}/${key}`);
  candidates.push(key, `writing/${key}`);
  for (const c of candidates) if (learnAssetMeta(c)) return learnAssetUrl(c);
  if (/^(ch_|asf_|call_|bakery_|fm_)/.test(key)) {
    const file = `${key}.png`;
    if (key.startsWith("asf_")) return `/assets/games/animal-sound-farm/${file}`;
    if (key.startsWith("ch_")) return `/assets/games/city-helpers/${file}`;
    if (key.startsWith("fm_")) return `/assets/games/feed-monster/${file}`;
    return `/assets/learn/${file}`;
  }
  return letterAsset(letter, key);
}

/** True when the manifest (or /public) can actually serve this asset name. */
export function hasAsset(letter, assetName) {
  if (!assetName) return false;
  const key = normalizeAssetKey(String(assetName));
  const L = String(letter || "a").toLowerCase();
  const own = key.match(/^([a-z])_/);
  return Boolean(
    learnAssetMeta(`letters/${L}/${key}`) ||
      (own && learnAssetMeta(`letters/${own[1]}/${key}`)) ||
      learnAssetMeta(key) ||
      learnAssetMeta(`writing/${key}`) ||
      /^(ch_|asf_|fm_)/.test(key),
  );
}

/** Per-letter accent, matching the stage chrome (alphabet world colour). */
export function useLetterAccent(letter) {
  return useMemo(() => {
    const world = worldContainingLetter(letter);
    const w = LEARN.world[world?.id];
    return w ? { accent: w.accent, deep: w.deep } : { accent: LEARN.color.blue, deep: LEARN.color.blueDeep };
  }, [letter]);
}

/** Fire onComplete at most once for this mount. */
export function useCompleteOnce(onComplete) {
  const done = useRef(false);
  return useCallback(() => {
    if (done.current) return;
    done.current = true;
    onComplete?.();
  }, [onComplete]);
}

export function useWrongFlash(ms = 420) {
  const [wrongId, setWrongId] = useState(null);
  const timer = useRef(null);
  const flash = useCallback(
    (id = "global") => {
      setWrongId(id);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setWrongId(null), ms);
    },
    [ms]
  );
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);
  return { wrongId, flash, isWrong: (id = "global") => wrongId === id };
}

/** Point-in-element test (drop fallbacks that need a rect the engine did not pick). */
export function hitTestEl(clientX, clientY, el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
}

// ── Shared drag + tap ──────────────────────────────────────────────────────

/**
 * `useDragEngine` plus the tap-then-tap fallback every engine needs.
 * `onDrop({ payload, targetId, targetData, viaTap })` is the single place a
 * game decides accept (return true → snap in) or reject (return false →
 * spring back). Tap flow: `pick(id, payload)` on the piece, `tapTarget(id,
 * data)` on the zone; both route through the same `onDrop`.
 */
export function useDragAndTap({ reduceMotion = false, sfx, onDrop } = {}) {
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;
  const [picked, setPicked] = useState(null); // { id, payload }
  const engine = useDragEngine({
    reduceMotion,
    onPickup: () => {
      setPicked(null);
      sfx?.("pickup", { volume: 0.5 });
    },
    onDrop: (info) => onDropRef.current?.({ ...info, viaTap: false }),
  });
  const pick = useCallback((id, payload) => {
    setPicked((p) => (p?.id === id ? null : { id, payload }));
  }, []);
  const clear = useCallback(() => setPicked(null), []);
  const tapTarget = useCallback(
    (targetId, targetData) => {
      if (!picked) return null;
      const r = onDropRef.current?.({ payload: picked.payload, targetId, targetData, viaTap: true });
      setPicked(null);
      return r;
    },
    [picked]
  );
  return { ...engine, picked, pick, clear, tapTarget, isPicked: (id) => picked?.id === id };
}

/** Props for a piece that drags or taps. Renders a real button with a focus ring. */
export function pieceButtonProps({ dragProps, payload, render, onTap, label, disabled = false, style }) {
  const dp = dragProps && !disabled ? dragProps(payload, render) : {};
  return {
    type: "button",
    "aria-label": label,
    disabled,
    onClick: disabled ? undefined : onTap,
    ...dp,
    className: "learn-piece",
    style: {
      appearance: "none",
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      font: "inherit",
      color: "inherit",
      cursor: disabled ? "default" : "grab",
      WebkitTapHighlightColor: "transparent",
      borderRadius: LEARN.radius.md,
      ...(dp.style || {}),
      ...style,
    },
  };
}

// ── Styles ─────────────────────────────────────────────────────────────────

/** Frosted white panel, legible over the world art. */
export function frosted(extra = {}) {
  return {
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: LEARN.radius.lg,
    boxShadow: `${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)`,
    border: `2px solid ${LEARN.color.line}`,
    ...extra,
  };
}

/**
 * Rounded header chip for a panel: an accent badge (letter / icon) next to a
 * short title, tinted with the panel's accent.
 */
export function headerChip({ accent = LEARN.color.blue, deep = LEARN.color.blueDeep, compact = false } = {}) {
  return {
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: compact ? 7 : 9,
      padding: compact ? "4px 12px 4px 4px" : "5px 14px 5px 5px",
      borderRadius: 999,
      background: `${accent}1c`,
      border: `2px solid ${accent}33`,
      color: deep,
      fontFamily: LEARN.font,
      fontWeight: 700,
      fontSize: compact ? 14 : 16,
      lineHeight: 1.15,
      maxWidth: "100%",
      minWidth: 0,
      boxSizing: "border-box",
    },
    badge: {
      width: compact ? 28 : 32,
      height: compact ? 28 : 32,
      borderRadius: compact ? 9 : 10,
      background: accent,
      color: "#fff",
      display: "grid",
      placeItems: "center",
      fontWeight: 700,
      fontSize: compact ? 16 : 18,
      flexShrink: 0,
      boxShadow: `0 2px 0 ${deep}`,
    },
    title: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  };
}

/** Soft caption pill under / over a picture card ("ant"). */
export function captionPill({ accent = LEARN.color.blue, deep = LEARN.color.blueDeep, size = "md" } = {}) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: size === "sm" ? "3px 12px" : "5px 16px",
    borderRadius: 999,
    background: "#fff",
    border: `2px solid ${accent}33`,
    boxShadow: `0 3px 0 ${deep}22, ${LEARN.shadow.card}`,
    fontFamily: LEARN.font,
    fontWeight: 700,
    fontSize: size === "sm" ? 16 : 20,
    color: LEARN.color.ink,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}

/** Drop-zone panel: frosted, tinted with the zone accent, glows while hovered. */
export function zoneStyle({ accent = LEARN.color.blue, hover = false, filled = false, wrong = false } = {}) {
  const ring = wrong ? LEARN.color.coral : hover ? accent : filled ? `${accent}99` : LEARN.color.line;
  const { border, ...base } = frosted();
  void border; // longhands only, so callers can override borderStyle without a shorthand clash
  return {
    ...base,
    background: hover ? `${accent}26` : "rgba(255,255,255,0.9)",
    borderWidth: 3,
    borderStyle: hover || wrong ? "solid" : "dashed",
    borderColor: ring,
    boxShadow: hover ? `0 0 0 6px ${accent}33, ${LEARN.shadow.float}` : LEARN.shadow.card,
    transition: "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
    transform: hover ? "scale(1.02)" : "scale(1)",
  };
}

/**
 * The inner "tray" of a zone or shelf: a soft inset well. Empty wells show a
 * dashed outline that lights up in the accent while a piece hovers or is
 * armed for a tap-drop.
 */
export function wellStyle({ accent = LEARN.color.blue, hover = false, armed = false, empty = true, wrong = false } = {}) {
  const lit = hover || (armed && empty);
  return {
    borderRadius: LEARN.radius.md,
    background: hover ? `${accent}1a` : "rgba(11,26,51,0.035)",
    boxShadow: hover ? `inset 0 0 0 3px ${accent}, inset 0 2px 8px rgba(11,26,51,0.05)` : "inset 0 2px 8px rgba(11,26,51,0.06)",
    border: empty || armed
      ? `2px dashed ${wrong ? LEARN.color.coral : lit ? accent : `${accent}66`}`
      : "2px solid transparent",
    transition: "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
    boxSizing: "border-box",
  };
}

export const gameShell = {
  root: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    fontFamily: LEARN.font,
    color: LEARN.color.ink,
    userSelect: "none",
    WebkitUserSelect: "none",
  },
  row: { display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", alignItems: "center" },
  /** Reflowing tray for pieces sized by --tile. */
  tray: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(calc(var(--tile) * 1.15), max-content))",
    justifyContent: "center",
    alignItems: "center",
    gap: "calc(var(--tile) * 0.14)",
    padding: "calc(var(--tile) * 0.12)",
  },
  zone: frosted({
    flex: 1,
    minHeight: "calc(var(--tile) * 1.6)",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  }),
  pill: {
    borderRadius: 999,
    border: "none",
    padding: "12px 20px",
    fontWeight: 700,
    fontSize: 18,
    cursor: "pointer",
    fontFamily: LEARN.font,
    color: "#fff",
    background: LEARN.color.blue,
    boxShadow: LEARN.shadow.sticker(LEARN.color.blueDeep),
    WebkitTapHighlightColor: "transparent",
  },
  tile: {
    width: "var(--tile)",
    height: "var(--tile)",
    borderRadius: "calc(var(--tile) * 0.26)",
    background: "#fff",
    border: `2px solid ${LEARN.color.line}`,
    boxShadow: LEARN.shadow.card,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    cursor: "grab",
    position: "relative",
  },
  word: { fontFamily: LEARN.font, fontWeight: 700, fontSize: 22, color: LEARN.color.ink, lineHeight: 1.1 },
};

// One global keyframe sheet, injected once (ShakeWrap used to inline a copy per instance).
let keyframesInjected = false;
export function ensureKeyframes() {
  if (keyframesInjected || typeof document === "undefined") return;
  keyframesInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-learn-keyframes", "");
  style.textContent = `
    @keyframes learn-shake {
      0%,100% { transform: translateX(0); }
      20% { transform: translateX(-6px); }
      40% { transform: translateX(6px); }
      60% { transform: translateX(-4px); }
      80% { transform: translateX(4px); }
    }
    @keyframes learn-pop {
      0% { transform: scale(1); }
      50% { transform: scale(1.12); }
      100% { transform: scale(1); }
    }
    @keyframes learn-fall {
      from { transform: translateY(-10%); opacity: 0.95; }
      to { transform: translateY(110%); opacity: 1; }
    }
    @keyframes learn-glow {
      0%,100% { box-shadow: 0 0 0 3px rgba(61,220,132,0.55), 0 10px 24px rgba(11,26,51,0.12); }
      50% { box-shadow: 0 0 0 8px rgba(61,220,132,0.22), 0 10px 24px rgba(11,26,51,0.12); }
    }
    @keyframes learn-breathe {
      0%,100% { transform: scale(1); }
      50% { transform: scale(1.04); }
    }
    .learn-piece:focus-visible { outline: 3px solid ${LEARN.color.blue}; outline-offset: 3px; }
    .learn-piece:not(:disabled):active > * { transform: scale(0.96) translateY(1px); }
    @media (hover: hover) {
      .learn-piece:not(:disabled):hover > * { transform: translateY(-3px); }
      .learn-piece:not(:disabled):active > * { transform: scale(0.96) translateY(1px); }
    }
    .learn-card:focus-visible { outline: 3px solid ${LEARN.color.blue}; outline-offset: 3px; }
    /* Chunky letter blocks: the bottom edge squashes flat while pressed. */
    .learn-piece:not(:disabled):active > .learn-block {
      transform: translateY(3px) scale(0.98);
      box-shadow: 0 1px 0 var(--deep, ${LEARN.color.blueDeep}), 0 6px 14px rgba(11,26,51,0.10) !important;
    }
    /* A rail slot that is waiting for a tapped piece lights up under the pointer. */
    @media (hover: hover) {
      .learn-slot-armed:hover {
        border-color: var(--accent, ${LEARN.color.blue}) !important;
        border-style: solid !important;
        background: var(--accent-soft, rgba(0,101,248,0.13)) !important;
        box-shadow: 0 0 0 6px var(--accent-ring, rgba(0,101,248,0.2)) !important;
      }
    }
  `;
  document.head.appendChild(style);
}

export function ShakeWrap({ shake, reduceMotion, children, style, ...rest }) {
  useEffect(ensureKeyframes, []);
  return (
    <div
      {...rest}
      style={{
        ...style,
        transition: reduceMotion ? "none" : "transform 80ms ease, box-shadow 120ms ease",
        animation: shake && !reduceMotion ? "learn-shake 0.42s ease" : undefined,
        boxShadow: shake ? `0 0 0 3px ${LEARN.color.coral}` : style?.boxShadow,
      }}
    >
      {children}
    </div>
  );
}

// ── Art ────────────────────────────────────────────────────────────────────

export function Pic({ letter, assetName, alt = "", size = 56, style }) {
  const src = resolveAsset(letter, assetName);
  if (!src) {
    return (
      <span style={{ fontSize: typeof size === "number" ? size * 0.35 : "calc(var(--tile) * 0.3)", fontWeight: 700, fontFamily: LEARN.font, ...style }}>
        {alt || "?"}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      decoding="async"
      onError={(e) => {
        e.currentTarget.style.visibility = "hidden";
      }}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
        pointerEvents: "none",
        filter: "drop-shadow(0 4px 6px rgba(11,26,51,0.14))",
        ...style,
      }}
    />
  );
}

/**
 * Sticker tile: white rounded card sized by --tile with the cutout at ~80%
 * (or a big letter glyph / any children). Purely presentational — wrap in a
 * button (see `pieceButtonProps`) to make it interactive.
 *   scale     multiplier on --tile
 *   selected  accent ring (tap-to-place pick state)
 *   solved    mint glow
 *   dim       muted (already used)
 *   chunky    letter-block look: top highlight + 3px bottom shadow in `deep`
 */
export function Tile({
  letter,
  assetName,
  alt = "",
  glyph,
  label,
  scale = 1,
  selected = false,
  solved = false,
  dim = false,
  accent = LEARN.color.blue,
  deep = LEARN.color.blueDeep,
  chunky = false,
  lift = true,
  wide = false,
  fill = false,   // size from the parent (100%) instead of --tile; art scales with it
  style,
  children,
}) {
  useEffect(ensureKeyframes, []);
  const size = fill ? "100%" : `calc(var(--tile) * ${scale})`;
  const hasArt = Boolean(assetName) && resolveAsset(letter, assetName);
  const showWord = !hasArt && !glyph && !children && alt;
  const artSize = fill ? (label ? "66%" : "80%") : `calc(var(--tile) * ${scale} * ${label ? 0.66 : 0.8})`;
  const block = chunky || glyph != null;
  const base = block
    ? `0 3px 0 ${deep}, ${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)`
    : `${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)`;
  return (
    <div
      style={{
        width: wide ? "auto" : size,
        minWidth: wide ? size : undefined,
        height: size,
        aspectRatio: fill ? "1" : undefined,
        padding: wide ? `0 calc(var(--tile) * ${scale} * 0.22)` : 0,
        borderRadius: `calc(var(--tile) * ${scale} * 0.24)`,
        background: dim
          ? "rgba(255,255,255,0.7)"
          : block
            ? "linear-gradient(180deg, #FFFFFF 0%, #F3F6FC 100%)"
            : "#fff",
        border: `2px solid ${selected ? accent : block ? `${deep}22` : LEARN.color.line}`,
        boxShadow: selected
          ? `0 0 0 5px ${accent}44, ${LEARN.shadow.float}${block ? `, 0 3px 0 ${deep}` : ""}`
          : solved
            ? `0 0 0 3px ${LEARN.color.mint}88, ${base}`
            : base,
        animation: solved ? "learn-glow 1.6s ease-in-out infinite" : undefined,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        overflow: "hidden",
        position: "relative",
        opacity: dim ? 0.55 : 1,
        transform: selected && lift ? "translateY(-4px)" : undefined,
        transition: "box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease, opacity 200ms ease",
        fontFamily: LEARN.font,
        color: LEARN.color.ink,
        flexShrink: 0,
        containerType: fill ? "inline-size" : undefined,
        boxSizing: "border-box",
        ...style,
      }}
    >
      {hasArt && (
        <Pic letter={letter} assetName={assetName} alt={alt} size={artSize} />
      )}
      {glyph != null && (
        <span
          aria-hidden="true"
          style={{
            fontSize: fill ? `${String(glyph).length > 1 ? 40 : 56}cqw` : `calc(var(--tile) * ${scale} * ${String(glyph).length > 1 ? 0.4 : 0.56})`,
            fontWeight: 700,
            lineHeight: 1,
            color: accent,
            textShadow: `0 2px 0 ${deep}22`,
          }}
        >
          {glyph}
        </span>
      )}
      {showWord && (
        <span style={{ fontSize: `calc(var(--tile) * ${scale} * 0.22)`, fontWeight: 700, textAlign: "center", padding: "0 6px" }}>
          {alt}
        </span>
      )}
      {children}
      {label && (
        <span style={{ fontSize: `calc(var(--tile) * ${scale} * 0.19)`, fontWeight: 700, color: LEARN.color.muted, lineHeight: 1 }}>
          {label}
        </span>
      )}
    </div>
  );
}

/**
 * Wooden-sticker letter block for word/sentence builders: white face, 2px
 * line border, a 4px bottom edge in the accent-deep colour, Fredoka glyph.
 * Presentational — wrap in a `pieceButtonProps` button for lift/press.
 *   text      the letter (or whole word when `wide`)
 *   scale     multiplier on --tile (height; width follows for `wide`)
 *   selected  armed for tap-to-place (accent ring, lifted)
 *   lit       celebration: filled with the accent, white glyph
 *   dim       already used
 */
export function LetterTile({ text, scale = 1, wide = false, selected = false, lit = false, dim = false, accent = LEARN.color.blue, deep = LEARN.color.blueDeep, style }) {
  useEffect(ensureKeyframes, []);
  const size = `calc(var(--tile) * ${scale})`;
  const glyph = wide
    ? `clamp(20px, calc(var(--tile) * ${scale} * 0.36), 30px)`
    : `clamp(26px, calc(var(--tile) * ${scale} * 0.46), 44px)`;
  return (
    <div
      className="learn-block"
      style={{
        "--deep": deep,
        width: wide ? "auto" : size,
        minWidth: wide ? size : undefined,
        height: size,
        padding: wide ? `0 calc(var(--tile) * ${scale} * 0.28)` : 0,
        borderRadius: `calc(var(--tile) * ${scale} * 0.22)`,
        background: lit
          ? `linear-gradient(180deg, ${accent} 0%, ${accent} 100%)`
          : "linear-gradient(180deg, #FFFFFF 0%, #F5F8FD 100%)",
        border: `2px solid ${selected ? accent : lit ? deep : LEARN.color.line}`,
        boxShadow: selected
          ? `0 0 0 5px ${accent}44, 0 4px 0 ${deep}, ${LEARN.shadow.float}`
          : `0 4px 0 ${deep}, ${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        flexShrink: 0,
        opacity: dim ? 0.55 : 1,
        transform: selected ? "translateY(-4px)" : undefined,
        transition: "transform 120ms ease, box-shadow 120ms ease, border-color 160ms ease, background 200ms ease, opacity 200ms ease",
        fontFamily: LEARN.font,
        fontWeight: 700,
        fontSize: glyph,
        lineHeight: 1,
        letterSpacing: wide ? "0.01em" : 0,
        whiteSpace: "nowrap",
        color: lit ? "#fff" : accent,
        textShadow: lit ? `0 2px 0 ${deep}55` : `0 2px 0 ${deep}1f`,
        ...style,
      }}
    >
      <span aria-hidden="true">{text}</span>
    </div>
  );
}

/** Small plus used between paired pictures. */
export function PlusIcon({ size = 20, color = LEARN.color.muted }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Staggered spring entrance for a piece; instant when reduceMotion. */
export function PopIn({ index = 0, reduceMotion = false, delay = 0.045, children, style, ...rest }) {
  return (
    <motion.div
      initial={reduceMotion ? false : { scale: 0.5, opacity: 0, y: 14 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 24, delay: index * delay }}
      style={{ display: "inline-flex", ...style }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * The one in-game count pill: small, frosted, `value` in an accent badge
 * followed by "of N". Floats at the top-right of the engine root unless
 * `floating={false}` (inline, e.g. inside a shelf header).
 */
export function CountPill({ value, total, accent = LEARN.color.blue, suffix, label, floating = true, style }) {
  return (
    <div
      aria-label={label ?? `${value} of ${total}${suffix ? ` ${suffix}` : ""}`}
      aria-live="polite"
      style={{
        position: floating ? "absolute" : "relative",
        top: floating ? 0 : undefined,
        right: floating ? 0 : undefined,
        zIndex: floating ? 6 : undefined,
        pointerEvents: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 11px 4px 5px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.9)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        border: `2px solid ${LEARN.color.line}`,
        boxShadow: LEARN.shadow.card,
        fontFamily: LEARN.font,
        fontSize: 14,
        fontWeight: 700,
        color: LEARN.color.ink,
        whiteSpace: "nowrap",
        lineHeight: 1,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <span
        style={{
          minWidth: 22, height: 22, padding: "0 6px", borderRadius: 999, background: accent, color: "#fff",
          display: "grid", placeItems: "center", fontSize: 13, boxSizing: "border-box",
        }}
      >
        {value}
      </span>
      <span>of {total}{suffix ? ` ${suffix}` : ""}</span>
    </div>
  );
}

/** "3 of 10" for in-game rounds (the stage owns lesson progress). */
export function RoundCounter({ done, total, accent, style }) {
  if (!total || total < 2) return null;
  return <CountPill value={Math.min(done + 1, total)} total={total} accent={accent} style={style} />;
}

export function SpeakerIcon({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10v4h3l4 4V6L7 10H4z" fill={color} />
      <path d="M15 9.5a3.5 3.5 0 010 5" />
      <path d="M17.5 7a7 7 0 010 10" />
    </svg>
  );
}

export function CheckIcon({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export function CrossIcon({ size = 18, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3.2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** Small down-arrow used by empty drop wells ("Drop here"). */
export function DropArrowIcon({ size = 18, color = LEARN.color.muted }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4v13M6 12l6 6 6-6" />
    </svg>
  );
}

/** Muted empty-state hint inside a drop well. */
export function DropHint({ text = "Drop here", accent = LEARN.color.blue, lit = false, compact = false }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2,
        fontFamily: LEARN.font, fontWeight: 600, fontSize: compact ? 13 : 15,
        color: lit ? accent : LEARN.color.muted, opacity: lit ? 1 : 0.8,
        transition: "color 160ms ease, opacity 160ms ease",
      }}
    >
      <DropArrowIcon color={lit ? accent : LEARN.color.muted} size={compact ? 16 : 20} />
      {text}
    </span>
  );
}

/** Legacy per-engine dots. The stage draws lesson progress; prefer RoundCounter. */
export function ProgressDots({ total, done, color = LEARN.color.mint }) {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center" }} aria-hidden>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: i < done ? color : "rgba(255,255,255,0.45)",
            boxShadow: i < done ? `0 0 0 2px ${color}55` : "none",
          }}
        />
      ))}
    </div>
  );
}

export function pickRoundItems(config, seed = 7) {
  const targets = config.targetItems || (config.items || []).filter((i) => i.isTarget !== false);
  const distractors =
    config.distractorItems || (config.items || []).filter((i) => i.isTarget === false);
  const tCount =
    config.roundTargetCount ??
    (config.targetHalf
      ? Math.ceil((config.roundSize || 8) / 2)
      : config.roundSize
        ? Math.ceil(config.roundSize / 2)
        : Math.min(5, targets.length));
  const dCount =
    config.roundDistractorCount ??
    (config.roundSize ? Math.floor(config.roundSize / 2) : Math.min(5, distractors.length));
  const t = seededShuffle(targets, seed).slice(0, Math.min(tCount, targets.length));
  const d = seededShuffle(distractors, seed + 17).slice(0, Math.min(dCount, distractors.length));
  return seededShuffle([...t, ...d], seed + 31);
}
